import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useSettings, useUpdateSettings } from '@/hooks/useSettings';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Bell, Eye, Mail, Calendar, Loader2, LogOut, Shield, ShieldCheck, UserPlus, Copy, Check, Sun, Moon, Monitor, Pencil, KeyRound, Smartphone, Trash2 } from 'lucide-react';
import { useMfa } from '@/hooks/useMfa';
import { MfaEnroll } from '@/components/auth/MfaEnroll';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useTheme } from 'next-themes';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@/hooks/useUserRole';
import { useMyInviteCodes, useGenerateInviteCode } from '@/hooks/useInviteCodes';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

export default function SettingsPage() {
  const { data: settings, isLoading } = useSettings();
  const { data: profile } = useProfile();
  const updateSettings = useUpdateSettings();
  const { signOut, user } = useAuth();
  const { isAdmin } = useIsAdmin();
  const isApproved = profile?.membership_status === 'approved';
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: inviteCodes } = useMyInviteCodes({ enabled: isApproved });
  const generateCode = useGenerateInviteCode();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const { theme, setTheme } = useTheme();
  const { factors, hasVerifiedFactor, isLoading: mfaLoading, refresh: refreshMfa } = useMfa();
  const [showMfaEnroll, setShowMfaEnroll] = useState(false);
  const [mfaEnrollMethod, setMfaEnrollMethod] = useState<'totp' | 'phone' | undefined>(undefined);
  const [showRemoveMfaDialog, setShowRemoveMfaDialog] = useState(false);
  const [removingMfa, setRemovingMfa] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const verifiedFactors = factors.filter((f) => f.status === 'verified');

  const handleRemoveMfa = async () => {
    setRemovingMfa(true);
    try {
      for (const factor of verifiedFactors) {
        const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
        if (error) throw error;
      }
      await refreshMfa();
      setShowRemoveMfaDialog(false);
      toast({ title: '2FA removed', description: 'Two-factor authentication has been disabled.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setRemovingMfa(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { confirmation: 'DELETE' },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({ title: 'Account deleted', description: 'Your account has been permanently deleted.' });
      await signOut();
      navigate('/auth');
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleGenerateCode = async () => {
    try {
      await generateCode.mutateAsync();
      toast({ title: 'Invite code generated', description: 'Share it with someone you want to invite.' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCopyCode = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedId(id);
    toast({ title: 'Copied!', description: `Invite code ${code} copied to clipboard.` });
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleToggle = async (
    key: 'email_notifications' | 'message_notifications' | 'event_reminders' | 'notify_post_likes' | 'notify_post_comments' | 'notify_connection_requests' | 'notify_mentions' | 'notify_new_messages' | 'notify_matchups' | 'notify_comment_replies' | 'notify_cohort_posts' | 'notify_comment_likes',
    value: boolean
  ) => {
    try {
      await updateSettings.mutateAsync({ [key]: value });
      toast({
        title: 'Settings updated',
        description: 'Your preferences have been saved.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleVisibilityChange = async (value: string) => {
    try {
      await updateSettings.mutateAsync({ profile_visibility: value });
      toast({
        title: 'Settings updated',
        description: 'Your visibility preferences have been saved.',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground text-sm">
          Manage your account and notification preferences
        </p>
      </div>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sun className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Appearance</CardTitle>
          </div>
          <CardDescription>
            Choose how QBN looks to you. Select a single theme, or sync with your system settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {([
              { value: 'light', label: 'Light', icon: Sun },
              { value: 'dark', label: 'Dark', icon: Moon },
              { value: 'system', label: 'System', icon: Monitor },
            ] as const).map((option) => {
              const Icon = option.icon;
              const isActive = theme === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => setTheme(option.value)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    isActive
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-muted-foreground/30 text-muted-foreground'
                  }`}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-sm font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Admin Access */}
      {isAdmin && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Admin Access</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/admin')} className="w-full sm:w-auto">
              <Shield className="h-4 w-4 mr-2" />
              Go to Admin Panel
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Invite Friends - only for approved members */}
      {isApproved && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              <CardTitle>Invite Friends</CardTitle>
            </div>
            <CardDescription>
              Generate invite codes to share with people you'd like to join QBN
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={handleGenerateCode}
              disabled={generateCode.isPending}
              className="w-full sm:w-auto"
            >
              {generateCode.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Generate Invite Code
            </Button>

            {inviteCodes && inviteCodes.length > 0 && (
              <div className="space-y-2">
                {inviteCodes.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <code className="font-mono font-semibold text-sm">
                        {invite.code}
                      </code>
                      {invite.is_used ? (
                        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                          Used
                        </span>
                      ) : (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Available
                        </span>
                      )}
                    </div>
                    {!invite.is_used && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopyCode(invite.code, invite.id)}
                        className="h-8 w-8 p-0"
                      >
                        {copiedId === invite.id ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Notifications</CardTitle>
          </div>
          <CardDescription>
            Choose what notifications you want to receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive email updates about community activity
              </p>
            </div>
            <Switch
              checked={settings?.email_notifications ?? true}
              onCheckedChange={(checked) =>
                handleToggle('email_notifications', checked)
              }
              disabled={updateSettings.isPending}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Message Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when you receive a new message
              </p>
            </div>
            <Switch
              checked={settings?.message_notifications ?? true}
              onCheckedChange={(checked) =>
                handleToggle('message_notifications', checked)
              }
              disabled={updateSettings.isPending}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Event Reminders</Label>
              <p className="text-sm text-muted-foreground">
                Receive reminders about upcoming events
              </p>
            </div>
            <Switch
              checked={settings?.event_reminders ?? true}
              onCheckedChange={(checked) =>
                handleToggle('event_reminders', checked)
              }
              disabled={updateSettings.isPending}
            />
          </div>

          {/* In-App Notification Preferences */}
          <div className="pt-4 mt-4 border-t border-border">
            <h4 className="text-sm font-semibold text-foreground mb-4">In-App Notifications</h4>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Likes on your posts</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone likes a post you created
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_post_likes ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_post_likes', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Comments on your posts</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone comments on a post you created
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_post_comments ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_post_comments', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Replies to your comments</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone replies to a comment you made
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_comment_replies ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_comment_replies', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Likes on your comments</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone likes a comment you made
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_comment_likes ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_comment_likes', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Posts in your city cohort</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone posts in your city's cohort feed
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_cohort_posts ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_cohort_posts', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Connection requests</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone sends you a connection request
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_connection_requests ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_connection_requests', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Mentions</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone mentions you in a post or comment
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_mentions ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_mentions', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Direct messages</Label>
                  <p className="text-sm text-muted-foreground">
                    When someone sends you a direct message
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_new_messages ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_new_messages', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">Weekly matchups</Label>
                  <p className="text-sm text-muted-foreground">
                    When you receive a new weekly matchup
                  </p>
                </div>
                <Switch
                  checked={settings?.notify_matchups ?? true}
                  onCheckedChange={(checked) =>
                    handleToggle('notify_matchups', checked)
                  }
                  disabled={updateSettings.isPending}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Privacy</CardTitle>
          </div>
          <CardDescription>
            Control who can see your profile
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Profile Visibility</Label>
            <Select
              value={settings?.profile_visibility ?? 'members'}
              onValueChange={handleVisibilityChange}
              disabled={updateSettings.isPending}
            >
              <SelectTrigger className="w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="members">All Members</SelectItem>
                <SelectItem value="connections">Connections Only</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {settings?.profile_visibility === 'connections'
                ? 'Only people you are connected with can view your full profile.'
                : 'All QBN members can view your profile.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {hasVerifiedFactor ? (
              <ShieldCheck className="h-5 w-5 text-green-500" />
            ) : (
              <Shield className="h-5 w-5 text-muted-foreground" />
            )}
            Two-Factor Authentication
            {hasVerifiedFactor && (
              <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                Enabled
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {hasVerifiedFactor
              ? 'Your account is protected with two-factor authentication.'
              : 'Add an extra layer of security to your account.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasVerifiedFactor ? (
            <>
              <div className="space-y-2">
                {verifiedFactors.map((factor) => (
                  <div
                    key={factor.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      {factor.factor_type === 'totp' ? (
                        <KeyRound className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Smartphone className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium">
                        {factor.factor_type === 'totp'
                          ? (factor.friendly_name || 'Authenticator App')
                          : 'Text Message (SMS)'}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">Active</Badge>
                  </div>
                ))}
              </div>

              {isAdmin && (
                <p className="text-xs text-muted-foreground">
                  Two-factor authentication is required for admin access.
                </p>
              )}

              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setShowRemoveMfaDialog(true)}
              >
                Remove 2FA
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Protect your account by requiring a second verification step when signing in.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  className="justify-start h-auto py-3 px-4"
                  onClick={() => {
                    setMfaEnrollMethod('totp');
                    setShowMfaEnroll(true);
                  }}
                >
                  <KeyRound className="h-4 w-4 mr-2 shrink-0" />
                  Enable with Authenticator App
                </Button>
                <Button
                  variant="outline"
                  className="justify-start h-auto py-3 px-4"
                  onClick={() => {
                    setMfaEnrollMethod('phone');
                    setShowMfaEnroll(true);
                  }}
                >
                  <Smartphone className="h-4 w-4 mr-2 shrink-0" />
                  Enable with Text Message (SMS)
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* MFA Enrollment Dialog */}
      {showMfaEnroll && (
        <div className="fixed inset-0 z-50">
          <MfaEnroll
            defaultMethod={mfaEnrollMethod}
            onSuccess={() => {
              setShowMfaEnroll(false);
              setMfaEnrollMethod(undefined);
              refreshMfa();
              toast({ title: '2FA enabled', description: 'Two-factor authentication is now active.' });
            }}
            onCancel={() => {
              setShowMfaEnroll(false);
              setMfaEnrollMethod(undefined);
            }}
          />
        </div>
      )}

      {/* Remove MFA Confirmation */}
      <Dialog open={showRemoveMfaDialog} onOpenChange={setShowRemoveMfaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Two-Factor Authentication?</DialogTitle>
            <DialogDescription>
              {isAdmin
                ? 'Warning: As an admin, you will need to re-enroll in 2FA the next time you access the admin panel.'
                : 'This will make your account less secure. You can re-enable it at any time.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveMfaDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveMfa}
              disabled={removingMfa}
            >
              {removingMfa ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Remove 2FA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account */}
      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
          <CardDescription>
            Manage your account settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex-1 min-w-0">
              <p className="font-medium">Email</p>
              {editingEmail ? (
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="New email address"
                    className="max-w-xs"
                  />
                  <Button
                    size="sm"
                    disabled={savingEmail || !newEmail}
                    onClick={async () => {
                      setSavingEmail(true);
                      try {
                        const { error } = await supabase.auth.updateUser({ email: newEmail });
                        if (error) throw error;
                        toast({ title: 'Confirmation link sent', description: 'Check your new email to confirm the change.' });
                        setEditingEmail(false);
                        setNewEmail('');
                      } catch (error: any) {
                        toast({ title: 'Error', description: error.message, variant: 'destructive' });
                      } finally {
                        setSavingEmail(false);
                      }
                    }}
                  >
                    {savingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingEmail(false); setNewEmail(''); }}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              )}
            </div>
            {!editingEmail && (
              <Button variant="ghost" size="sm" onClick={() => setEditingEmail(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Change
              </Button>
            )}
          </div>

          <Button
            variant="outline"
            onClick={handleSignOut}
            className="w-full sm:w-auto text-destructive hover:text-destructive"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </div>
          <CardDescription>
            Permanently delete your account and all associated data including your profile,
            posts, comments, connections, messages, and uploaded files. This action cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteAccountDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete Account Confirmation */}
      <Dialog open={showDeleteAccountDialog} onOpenChange={(open) => {
        setShowDeleteAccountDialog(open);
        if (!open) setDeleteConfirmation('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This will permanently delete your account and all your data. This action
              cannot be undone. Type <span className="font-mono font-bold">DELETE</span> below to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder='Type "DELETE" to confirm'
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            disabled={deletingAccount}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteAccountDialog(false);
              setDeleteConfirmation('');
            }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmation !== 'DELETE' || deletingAccount}
            >
              {deletingAccount ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
